import { CheckCircle, ChevronDown, RefreshCw, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

interface VerificationControlsProps {
  documentId: string;
  status: string;
  onVerify: (id: string) => Promise<void>;
  onReject: (id: string, reason: string) => Promise<void>;
  onReupload: (id: string, reason: string) => Promise<void>;
}

type ModalType = 'reject' | 'reupload' | null;

interface DropdownPosition {
  top: number;
  left: number;
}

export function VerificationControls({
  documentId,
  status,
  onVerify,
  onReject,
  onReupload,
}: VerificationControlsProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [reason, setReason] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition>({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const isActionable = status === 'uploaded' || status === 'flagged';

  // Position dropdown and close on click outside
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 176; // w-44 = 11rem = 176px
      const menuHeight = 112; // approximate height of 3 items
      const spaceBelow = window.innerHeight - rect.bottom;
      const opensUpward = spaceBelow < menuHeight && rect.top > menuHeight;

      setDropdownPos({
        top: opensUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
        left: Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
      });
    }
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  const handleVerify = useCallback(async () => {
    setIsOpen(false);
    setIsLoading(true);
    try {
      await onVerify(documentId);
    } finally {
      setIsLoading(false);
    }
  }, [documentId, onVerify]);

  const handleSubmitReason = useCallback(async () => {
    if (!reason.trim()) return;

    setIsLoading(true);
    try {
      if (activeModal === 'reject') {
        await onReject(documentId, reason);
      } else if (activeModal === 'reupload') {
        await onReupload(documentId, reason);
      }
      setActiveModal(null);
      setReason('');
    } finally {
      setIsLoading(false);
    }
  }, [activeModal, documentId, onReject, onReupload, reason]);

  const handleOpenReject = useCallback(() => {
    setIsOpen(false);
    setActiveModal('reject');
  }, []);

  const handleOpenReupload = useCallback(() => {
    setIsOpen(false);
    setActiveModal('reupload');
  }, []);

  if (!isActionable) {
    return null;
  }

  return (
    <>
      <button
        ref={buttonRef}
        type='button'
        disabled={isLoading}
        onClick={handleToggle}
        className={cn(
          'inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md',
          'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 transition-colors',
        )}>
        {t('documents.actions.actions')}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {/* Dropdown menu — rendered via portal to escape overflow:hidden */}
      {isOpen
        ? createPortal(
            <div
              ref={menuRef}
              style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left }}
              className='z-[100] w-44 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5'>
              <button
                type='button'
                onClick={() => void handleVerify()}
                className='flex w-full items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-green-50 hover:text-green-700'>
                <CheckCircle className='h-3.5 w-3.5' />
                {t('documents.actions.verify')}
              </button>
              <button
                type='button'
                onClick={handleOpenReject}
                className='flex w-full items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-red-50 hover:text-red-700'>
                <XCircle className='h-3.5 w-3.5' />
                {t('documents.actions.reject')}
              </button>
              <button
                type='button'
                onClick={handleOpenReupload}
                className='flex w-full items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-orange-50 hover:text-orange-700'>
                <RefreshCw className='h-3.5 w-3.5' />
                {t('documents.actions.requestReupload')}
              </button>
            </div>,
            document.body,
          )
        : null}

      {/* Reason Modal */}
      {activeModal ? (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
          <div className='bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              {activeModal === 'reject' ? t('documents.rejectModal.title') : t('documents.reuploadModal.title')}
            </h3>

            <label className='block text-sm font-medium text-gray-700 mb-2'>
              {activeModal === 'reject'
                ? t('documents.rejectModal.reasonLabel')
                : t('documents.reuploadModal.reasonLabel')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                activeModal === 'reject'
                  ? t('documents.rejectModal.reasonPlaceholder')
                  : t('documents.reuploadModal.reasonPlaceholder')
              }
              rows={4}
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            />

            <div className='flex justify-end gap-3 mt-4'>
              <button
                type='button'
                onClick={() => {
                  setActiveModal(null);
                  setReason('');
                }}
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200'>
                {t('documents.rejectModal.cancel')}
              </button>
              <button
                type='button'
                disabled={!reason.trim() || isLoading}
                onClick={() => void handleSubmitReason()}
                className={cn(
                  'px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50',
                  activeModal === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700',
                )}>
                {activeModal === 'reject' ? t('documents.rejectModal.confirm') : t('documents.reuploadModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
